/**
 * Translation keys for visibility by weight.
 * @type {Object}
 */
const VISIBILITY_BY_WEIGHT = {
  FREE: 'professionalPubli.publish.plansSelector.FREE.visibility',
  SIMPLE: 'professionalPubli.publish.plansSelector.SIMPLE.visibility',
  HIGHLIGHTED: 'professionalPubli.publish.plansSelector.HIGHLIGHTED.visibility',
  SUPERHIGHLIGHTED: 'professionalPubli.publish.plansSelector.SUPERHIGHLIGHTED.visibility'
};

/**
 * Adapt a plan to the publication form.
 * @param {Object} plan - Plan object.
 * @param {Function} t - Translation function.
 * @param {boolean} isSelected - Indicates if the plan is selected.
 * @param {boolean} isEdition - Indicates if a plan is being edited.
 * @returns {Object} Adapted plan object.
 */
const adaptPlan = (plan, t, isSelected, isEdition) => {
  const { publication_plan, available } = plan;

  return {
    id: publication_plan.publication_plan_id,
    name: publication_plan.name,
    available: isEdition && isSelected ? Number(available) + 1 : Number(available),
    visibility: t(VISIBILITY_BY_WEIGHT[publication_plan.publicationType]),
    selected: isSelected
  };
};

/**
 * Normalize planSaved to plans format.
 * @param {Object} planSaved - Plan object.
 * @returns {Object} Normalized plan object.
 */
const normalizePlanSaved = (planSaved = null) => {
  if (!planSaved) return null;
  return {
    available: '1', // Default value when selected plan has no available plans.
    publication_plan: planSaved
  };
};

/**
 * Adapt available plans for the publication form.
 * @param {Array} plans - Array of available plans.
 * @param {Function} t - Translation function.
 * @param {Object} planSaved - Selected plan.
 * @param {boolean} isEdition - Indicates if a plan is being edited.
 * @param {Array} branches - Array of branches.
 * @param {number} currentBranch - Current branch ID.
 * @param {string} postingStatus - Current posting status (e.g., 'ONLINE', 'DRAFT').
 * @returns {Array} Array of adapted plans.
 */
const adaptAvailablePlans = (
  plans = [],
  t = () => {},
  planSaved = null,
  isEdition = false,
  branches = [],
  currentBranch = null,
  postingStatus = null
) => {
  const originalBranch = branches.find(branch => branch.isOwner)?.branchId || null;
  const branchChanged = currentBranch !== originalBranch;
  const plansEmpty = plans.length === 0;
  const normalizedPlanSaved = normalizePlanSaved(planSaved);

  // Caso 1:
  // - Cuando la branch actual no tiene planes disponibles pero es un aviso ONLINE: retornamos el plan del aviso ONLINE
  if (!branchChanged && plansEmpty && postingStatus === 'ONLINE') {
    return [adaptPlan(normalizedPlanSaved, t, true)];
  }

  // Caso 2:
  // - Cuando la branch no tiene planes disponibles: retornamos []
  if (plansEmpty) {
    return [];
  }

  const planExists = plans.some(plan => plan.publication_plan.publication_plan_id === planSaved?.publication_plan_id);

  // Caso 3:
  // - Cuando la branch actual tiene planes disponibles y el plan del aviso coincide: lo marcamos como seleccionado
  // - Cuando se cambia la branch y tiene planes disponibles pero el plan del aviso no coincide: mostramos los planes disponibles
  const adaptedPlans = plans.map(plan => {
    const isPlanSaved = planSaved?.publication_plan_id === plan.publication_plan.publication_plan_id;
    const shouldSelect = isPlanSaved && planExists && !branchChanged;
    return adaptPlan(plan, t, shouldSelect, isEdition);
  });

  // Caso 4:
  // - Cuando la branch actual tiene planes disponibles pero el plan del aviso no coincide: agregamos plan del aviso al principio
  if (planSaved && !planExists && !branchChanged) {
    adaptedPlans.unshift(adaptPlan(normalizedPlanSaved, t, true));
  }

  // Caso 5:
  // - Si no hay plan seleccionado, seleccionamos el primero disponible
  const anySelected = adaptedPlans.some(p => p.selected);
  if (adaptedPlans.length > 0 && !anySelected) {
    adaptedPlans[0].selected = true;
  }

  return adaptedPlans;
};

export default adaptAvailablePlans;
